import { useState, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Mail, ArrowLeft, CheckCircle2, Loader2, AlertCircle } from 'lucide-react'
import { authApi } from '@/api/auth'
import { getErrorMessage } from '@/lib/api'
import { BRAND } from '@/config/brand'

export default function ForgotPasswordPage() {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const mutation = useMutation({
    mutationFn: () => authApi.forgotPassword({ email }),
    onSuccess: () => {
      setSubmitted(true)
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    mutation.mutate()
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="w-full max-w-md">
        {/* Logo Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-8 text-center">
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg bg-gradient-to-br ${BRAND.gradient}`}>
              <span className="text-white text-4xl font-bold">{BRAND.initial}</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">
              {isArabic ? BRAND.nameAr : BRAND.name}
            </h1>
            <p className="text-blue-100 text-sm">
              {isArabic ? BRAND.taglineAr : BRAND.tagline}
            </p>
          </div>

          {/* Body */}
          <div className="p-8">
            {!submitted ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t('forgotPassword.title')}
                  </h2>
                  <p className="text-gray-600 text-sm">
                    {t('forgotPassword.subtitle')}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('auth.email')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input"
                      placeholder="you@example.com"
                      dir="ltr"
                      autoComplete="email"
                      required
                      disabled={mutation.isPending}
                    />
                  </div>

                  {mutation.isError ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{getErrorMessage(mutation.error)}</p>
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    disabled={mutation.isPending || !email.trim()}
                    className="w-full btn-primary flex items-center justify-center gap-2 py-3"
                  >
                    {mutation.isPending ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t('forgotPassword.sending')}
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        {t('forgotPassword.sendResetLink')}
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="text-sm text-gray-600 hover:text-primary-600 inline-flex items-center gap-1"
                  >
                    <ArrowLeft className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
                    {t('forgotPassword.backToLogin')}
                  </Link>
                </div>
              </>
            ) : (
              /* Success State */
              <div className="text-center py-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  {t('forgotPassword.checkEmail')}
                </h2>
                <p className="text-gray-600 mb-2">
                  {t('forgotPassword.emailSentTo')}
                </p>
                <p className="text-gray-900 font-semibold mb-6" dir="ltr">
                  {email}
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 text-start mb-6">
                  <p className="mb-2 font-semibold">{t('forgotPassword.nextSteps')}</p>
                  <ul className="space-y-1 list-disc list-inside text-blue-800">
                    <li>{t('forgotPassword.step1')}</li>
                    <li>{t('forgotPassword.step2')}</li>
                    <li>{t('forgotPassword.step3')}</li>
                  </ul>
                </div>

                <p className="text-xs text-gray-500 mb-6">
                  ⏱️ {t('forgotPassword.linkExpires')}
                </p>

                <Link
                  to="/login"
                  className="text-sm text-primary-600 hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
                  {t('forgotPassword.backToLogin')}
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 mt-6">
          {BRAND.name} © {BRAND.copyrightYear} - {isArabic ? BRAND.copyrightSuffixAr : BRAND.copyrightSuffix}
        </p>
      </div>
    </div>
  )
}
